import type { CSSProperties, ReactNode } from 'react';
import { TriangleAlert, X, type LucideIcon } from 'lucide-react';
import type { LatLngTuple } from 'leaflet';
import { GoogleMapsLink } from '@/components/map/GoogleMapsLink';
import { EMPTY_VALUE } from '@/constants/app.constants';
import {
  getGoogleMapsDirectionsUrl,
  getGoogleMapsUrl,
  type GoogleMapsTravelMode,
} from '@/components/map/googleMaps';
import { cn } from '@/lib/utils';
import { formatShortDay } from '../utils/dayLabels';

const TINTED_BODY_CLASS = cn(
  'bg-[color:color-mix(in_srgb,var(--entry-color)_8%,var(--popover))]',
  'ring-[color:color-mix(in_srgb,var(--entry-color)_25%,transparent)]',
  '**:border-[color:color-mix(in_srgb,var(--entry-color)_20%,transparent)]',
);

type EntryCardStyle = CSSProperties & Record<'--entry-color', string>;

interface MoEntryDetailLineProps {
  icon: LucideIcon;
  children: ReactNode;
}

export function MoEntryDetailLine({ icon: Icon, children }: MoEntryDetailLineProps) {
  return (
    <span className="flex items-start gap-1.5 text-xs text-foreground/80">
      <Icon className="mt-px size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">{children}</span>
    </span>
  );
}

interface MoEntryStatProps {
  label: string;
  value: string | null;
  hint: string;
  isWarning?: boolean;
}

export function MoEntryStat({ label, value, hint, isWarning = false }: MoEntryStatProps) {
  return (
    <div title={hint} className="flex min-w-0 flex-col items-center gap-0.5 px-1.5 py-2">
      <span className="text-3xs font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </span>
      <span
        className={cn(
          'flex max-w-full items-center gap-0.5 text-sm leading-tight font-semibold whitespace-nowrap tabular-nums',
          isWarning && 'text-warning-foreground',
          value === null && 'font-normal text-muted-foreground',
        )}
      >
        {isWarning && <TriangleAlert className="size-3 shrink-0" aria-hidden />}
        {value ?? EMPTY_VALUE}
      </span>
    </div>
  );
}

interface MoEntryStatGridProps {
  children: ReactNode;
}

export function MoEntryStatGrid({ children }: MoEntryStatGridProps) {
  return (
    <div className="grid grid-cols-[repeat(3,minmax(max-content,1fr))] divide-x rounded-md bg-muted/60 max-lg:border">
      {children}
    </div>
  );
}

export interface MapsDirections {
  from: LatLngTuple;
  travelMode: GoogleMapsTravelMode;
}

interface MoEntryMapsLinkProps {
  position: LatLngTuple;
  directions?: MapsDirections | null;
}

export function MoEntryMapsLink({ position, directions }: MoEntryMapsLinkProps) {
  return (
    <GoogleMapsLink
      href={
        directions
          ? getGoogleMapsDirectionsUrl(directions.from, position, directions.travelMode)
          : getGoogleMapsUrl(position[0], position[1])
      }
      label="Maps"
      title={directions ? 'Check this leg in Google Maps' : 'Open in Google Maps'}
      className="ml-auto"
    />
  );
}

interface MoEntryCardProps {
  headerColor: string;
  headerMarker: ReactNode;
  heading: string;
  date: string;
  closeLabel: string;
  onClose?: (() => void) | undefined;
  hasTintedBody?: boolean | undefined;
  className?: string | undefined;
  children: ReactNode;
}

export function MoEntryCard({
  headerColor,
  headerMarker,
  heading,
  date,
  closeLabel,
  onClose,
  hasTintedBody = false,
  className,
  children,
}: MoEntryCardProps) {
  const cardStyle: EntryCardStyle = { '--entry-color': headerColor };

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-lg ring-1 ring-foreground/5',
        hasTintedBody && TINTED_BODY_CLASS,
        className,
      )}
      style={cardStyle}
    >
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 text-white"
        style={{ background: headerColor }}
      >
        <span
          className="flex size-5 shrink-0 items-center justify-center rounded-full bg-white text-3xs font-bold"
          style={{ color: headerColor }}
        >
          {headerMarker}
        </span>
        <span className="text-xs font-semibold">{heading}</span>
        <span className="ml-auto text-2xs font-bold text-white">{formatShortDay(date)}</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="-mr-1 flex size-5 shrink-0 items-center justify-center rounded text-white/80 transition-colors hover:bg-white/20 hover:text-white max-lg:-my-2 max-lg:-mr-2 max-lg:size-10"
          >
            <X className="size-3.5 max-lg:size-4" aria-hidden />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2 px-2.5 py-2 max-lg:gap-2.5 max-lg:px-3 max-lg:py-2.5">
        {children}
      </div>
    </div>
  );
}
